/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useState, type ReactNode } from 'react';
import { CircleCheck } from 'lucide-react';
import { Button } from './Button';
import { Icon } from './Icon';

/**
 * Unsaved edits over a saved object. `values` is the saved object with the person's edits on top; `changed` lists the
 * keys whose edit differs from the saved value (an edit typed back to the saved value is not a change). When the saved
 * object is reloaded after a save, edits equal to the new values stop counting by themselves.
 */
export function useDirtyFields<T extends Record<string, unknown>>(saved: T) {
  const [edits, setEdits] = useState<Partial<T>>({});
  const keys = Object.keys(edits) as Array<keyof T>;
  const changed = keys.filter((key) => !Object.is(edits[key], saved[key]));
  const values = { ...saved, ...edits } as T;
  return {
    values,
    changed,
    isChanged: (key: keyof T) => changed.includes(key),
    /** The saved value of `key`, for "Unsaved · was 150". */
    was: (key: keyof T) => saved[key],
    set: <K extends keyof T>(key: K, value: T[K]) => setEdits((current) => ({ ...current, [key]: value })),
    discard: () => setEdits({}),
  };
}

export interface SaveBarProps {
  /** How many fields are unsaved; the bar shows only while there is at least one (or an error). */
  changes: number;
  onSave: () => void;
  onDiscard: () => void;
  saving?: boolean;
  /** The service's refusal of the last save, in its own words. */
  error?: ReactNode;
  saveLabel?: string;
  /** A line beside the count: what the unsaved changes go on top of ("Revision 42 · saved today 16:58 by …"). */
  meta?: ReactNode;
  /** The last save's confirmation, shown in the bar's place while nothing is unsaved ("Saved as revision 43. …"). */
  saved?: ReactNode;
  className?: string;
}

/**
 * The page's one Save: "2 unsaved changes · Discard · Save", sticky at the bottom of the page, in place of a Save button
 * on every card. With `saved`, the bar stays after a save as its short confirmation until the next change.
 */
export function SaveBar({ changes, onSave, onDiscard, saving = false, error, saveLabel = 'Save changes', meta, saved, className }: SaveBarProps) {
  if (changes <= 0 && !error) {
    if (!saved) return null;
    return (
      <div className={['ui-savebar', 'is-saved', className ?? ''].filter(Boolean).join(' ')} role="status">
        <Icon icon={CircleCheck} size="md" />
        <span className="ui-savebar-text">{saved}</span>
      </div>
    );
  }
  return (
    <div className={['ui-savebar', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')} role="region" aria-label="Unsaved changes">
      <span className="ui-savebar-text" aria-live="polite">
        {changes > 0 ? `${changes} unsaved change${changes === 1 ? '' : 's'}` : 'Nothing to save'}
      </span>
      {meta ? meta : null}
      {error ? <span className="ui-savebar-error" role="alert">{error}</span> : null}
      <span className="ui-savebar-actions">
        <Button variant="ghost" onClick={onDiscard} disabled={saving || changes <= 0}>Discard</Button>
        <Button variant="primary" onClick={onSave} busy={saving} disabled={changes <= 0}>{saveLabel}</Button>
      </span>
    </div>
  );
}
